import React, { ReactElement } from "react";
import { List as RPDFCList, ListItem as RPDFCListItem } from "@surge-global-engineering/react-pdf-components";
import { PdfTheme, PdfSlateEndnote, PdfSlateListItem, PdfSlateImage } from "../types";
import Image from "./Image";
import BaseChildNode from "./BaseChildNode";

interface ListProps {
  theme: PdfTheme;
  type: "ol" | "ul";
  endnotes: PdfSlateEndnote[];
  dropcap?: ReactElement[];

  /**
   * showFirstSentenceFormatting - A value that can be defined using "Chapter Level" configuration
   * to toggle first sentence formatting on/off.
   *
   * @type {boolean}
   */
  showFirstSentenceFormatting: boolean;
  endNoteOffset?: number;
}

const List: React.FC<ListProps> = ({ theme, type, endnotes, children, showFirstSentenceFormatting, endNoteOffset }) => {
    const _children = children as PdfSlateListItem[];
    const { baseFontSize, printBaseFont , paragraph:{ paragraphSpacing } } = theme.properties;
    
    return (
        <RPDFCList type={type}>
            {
                _children.map((listItem, listItemIndex) => {
                    const { children: listItemChildren } = listItem as PdfSlateListItem;
                    const Children: ReactElement[] = listItemChildren.map((listItemChild, listItemChildIndex) => {
                        const { children: listItemChildChildren, type: listItemChildType } = listItemChild;

                        if (["ol", "ul"].includes(listItemChildType)) {
                            return (
                              <List
                                key={`li-l-${
                                  listItemChildIndex + listItemIndex
                                }`}
                                endnotes={endnotes}
                                theme={theme}
                                type={listItemChildType as "ol" | "ul"}
                                showFirstSentenceFormatting={showFirstSentenceFormatting}
                              >
                                {listItemChildChildren}
                              </List>
                            );
                        }

                        if (listItemChildChildren.length) {
                            const imageNode = listItemChildChildren[0] as PdfSlateImage;

                            if (imageNode.type === "image") {
                                return (
                                    <Image key={`image-${listItemChildIndex + listItemIndex}`} theme={theme} imageNode={imageNode} />
                                );
                            }
                        }

                        return (
                          <BaseChildNode
                            key={`li-base-parent-${
                              listItemChildIndex + listItemIndex
                            }`}
                            theme={theme}
                            nodeIndex={listItemChildIndex + listItemIndex + 5}
                            endNotes={endnotes}
                            parentType={listItemChildType}
                            noIndent
                            showFirstSentenceFormatting={showFirstSentenceFormatting}
                            endNoteOffset={endNoteOffset}
                          >
                            {listItemChildChildren}
                          </BaseChildNode>
                        );
                    });
                    return (
                        <RPDFCListItem key={`li-${listItemIndex}`} style={{ fontFamily: printBaseFont, fontSize: baseFontSize , lineHeight: paragraphSpacing }}>
                            {Children[0]}
                            {Children[1]}
                        </RPDFCListItem>
                    );
                })
            }
        </RPDFCList>
    );
};

export default List;
